'use client'

import { resolvePcpContactMode, type PcpContactMode } from '@/utilities/pcpValidation'

export const PATIENT_PORTAL_PCP_INFORMATION_FLAG_KEY = 'patient-portal-pcp-information'
export const CLIENT_PORTAL_PCP_INFORMATION_FLAG_KEY = 'client-portal-pcp-information'

/** Shared JSON fields for both portal PCP-information flags. */
export type PcpInformationFlagValue = {
  enabled: boolean
  /**
   * When false, hide the share-results question, show that results are sent automatically,
   * and send `send_fax_results_to_pcp: true`.
   */
  showShareResultsWithPcpQuestion: boolean
  /** When false, hide the need-PCP-help question and send `need_pcp_help: null`. */
  showNeedPcpHelpQuestion: boolean
  /**
   * Which PCP contact channel(s) to show and require in the form (configured-channel mode).
   * Kit registration submit still persists any valid fax / complete address when present
   * (e.g. autofill of a hidden channel). Derived from LD `showPcpFax` / `showPcpMail`
   * (both false → fax fallback).
   */
  pcpContactMode: PcpContactMode
}

export type PatientPcpInformationFlagValue = PcpInformationFlagValue & {
  /** When false, hide the autofill question and send `allow_autofill: false`. Patient Portal only. */
  allowAutofill: boolean
}

export const DEFAULT_PCP_INFORMATION_FLAG: PcpInformationFlagValue = {
  enabled: false,
  showShareResultsWithPcpQuestion: true,
  showNeedPcpHelpQuestion: true,
  pcpContactMode: 'either',
}

export const DEFAULT_PATIENT_PCP_INFORMATION_FLAG: PatientPcpInformationFlagValue = {
  ...DEFAULT_PCP_INFORMATION_FLAG,
  allowAutofill: false,
}

export function parsePcpInformationFlag(flag: unknown): PcpInformationFlagValue {
  if (typeof flag !== 'object' || flag === null) {
    return { ...DEFAULT_PCP_INFORMATION_FLAG }
  }
  const r = flag as Record<string, unknown>
  return {
    enabled: r.enabled === true,
    showShareResultsWithPcpQuestion: r.showShareResultsWithPcpQuestion !== false,
    showNeedPcpHelpQuestion: r.showNeedPcpHelpQuestion !== false,
    pcpContactMode: resolvePcpContactMode(r.showPcpFax !== false, r.showPcpMail !== false),
  }
}

export function parsePatientPcpInformationFlag(flag: unknown): PatientPcpInformationFlagValue {
  if (typeof flag !== 'object' || flag === null) {
    return { ...DEFAULT_PATIENT_PCP_INFORMATION_FLAG }
  }
  const r = flag as Record<string, unknown>
  return {
    ...parsePcpInformationFlag(flag),
    allowAutofill: r.allowAutofill === true,
  }
}
